import type { ExpressionSpecification, GeoJSONSource, LayerSpecification, MapMouseEvent } from 'maplibre-gl';
import { useCallback, useEffect, useRef } from 'react';
import { useTheme } from '@/providers/theme-provider';
import type { LiveVehicle } from '@/lib/vehicle-status';
import { FleetAnimator, type FleetInput } from './fleet-animator';
import { PIN_HEAD_RADIUS, PIN_SHAFT, PIN_TIP_OFFSET, PULSE_ICON } from './fleet-icons';
import { useMap } from './map-context';
import { MAP_FONT, mapPalette, statusHex } from './map-palette';

/**
 * Two sources on purpose.
 *
 * `cluster: true` makes every setData re-run supercluster in the worker. Feeding
 * a clustered source at animation rate means the worker never finishes and the
 * source never reports loaded, so nothing tiles and nothing draws — markers just
 * silently vanish. So the animated markers live on an unclustered source that is
 * cheap to re-tile 60 times a second, and clustering gets its own source updated
 * on a slow throttle. Zoom decides which of the two is visible, so they never
 * draw the same vehicle twice.
 */
const POINTS_SOURCE = 'vt-fleet';
const CLUSTER_SOURCE = 'vt-fleet-clustered';

const CLUSTER_MAX_ZOOM = 10;
/** Clusters only matter when zoomed out, where per-frame precision is invisible. */
const CLUSTER_REFRESH_MS = 900;

/**
 * Marker scale by zoom. Declared once because two things need it: the layout
 * expression that renders the pin, and the hit test that has to know how tall
 * the pin currently stands.
 */
const MARKER_SIZE_MIN = 1.05;
const MARKER_SIZE_MAX = 1.5;
const MARKER_SIZE_MAX_ZOOM = 16;

function markerSizeAt(zoom: number): number {
  const t = Math.min(
    1,
    Math.max(0, (zoom - CLUSTER_MAX_ZOOM) / (MARKER_SIZE_MAX_ZOOM - CLUSTER_MAX_ZOOM)),
  );
  return MARKER_SIZE_MIN + (MARKER_SIZE_MAX - MARKER_SIZE_MIN) * t;
}

export const FLEET_LAYERS = {
  clusters: 'vt-fleet-clusters',
  clusterCount: 'vt-fleet-cluster-count',
  clusterSingles: 'vt-fleet-cluster-singles',
  glow: 'vt-fleet-glow',
  ring: 'vt-fleet-ring',
  pulse: 'vt-fleet-pulse',
  selected: 'vt-fleet-selected',
  markers: 'vt-fleet-markers',
  labels: 'vt-fleet-labels',
} as const;

export function VehicleLayer({
  vehicles,
  selectedId,
  onSelect,
  onHover,
  hoverAnchorRef,
}: {
  vehicles: LiveVehicle[];
  selectedId: string | null;
  onSelect: (vehicleId: string | null) => void;
  /** Fires with the hovered vehicle id, or null when the cursor leaves. */
  onHover?: (vehicleId: string | null) => void;
  /** Positioned imperatively each frame so the card tracks its moving marker. */
  hoverAnchorRef?: React.RefObject<HTMLDivElement | null>;
}) {
  const { map, ready, styleEpoch } = useMap();
  const { resolved } = useTheme();
  const animator = useRef(new FleetAnimator());
  const frame = useRef<number | null>(null);
  const running = useRef(false);
  const lastClusterPush = useRef(-Infinity);
  // The anchor placer runs inside an animation frame and must see the current
  // selection without being re-created (and thus restarting the loop) for it.
  const selectedRef = useRef<string | null>(selectedId);
  useEffect(() => {
    selectedRef.current = selectedId;
  }, [selectedId]);

  const hoveredRef = useRef<string | null>(null);
  const onHoverRef = useRef(onHover);
  useEffect(() => {
    onHoverRef.current = onHover;
  }, [onHover]);

  /**
   * Anchors the hover card to where the marker is actually drawn this frame,
   * rather than to the cursor, so the card stays glued to a moving vehicle.
   */
  const placeHoverAnchor = useCallback(() => {
    const element = hoverAnchorRef?.current;
    if (!element || !map) return;
    const id = hoveredRef.current;
    const position = id ? animator.current.positionOf(id) : null;
    if (!position) {
      element.style.visibility = 'hidden';
      return;
    }
    const point = map.project([position.lng, position.lat]);
    // Anchor at the top of the pin, not its coordinate, or the card that opens
    // above the anchor lands on top of the marker it is describing.
    const top = point.y - PIN_SHAFT * markerSizeAt(map.getZoom());
    element.style.visibility = 'visible';
    element.style.transform = `translate3d(${Math.round(point.x)}px, ${Math.round(top)}px, 0)`;
  }, [map, hoverAnchorRef]);

  /* Sources and layers — re-added after every basemap swap. */
  useEffect(() => {
    if (!map || !ready) return;
    const palette = mapPalette(resolved);
    const status = statusHex(resolved);
    const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

    if (!map.getSource(POINTS_SOURCE)) {
      map.addSource(POINTS_SOURCE, { type: 'geojson', data: empty });
    }
    if (!map.getSource(CLUSTER_SOURCE)) {
      map.addSource(CLUSTER_SOURCE, {
        type: 'geojson',
        data: empty,
        cluster: true,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
        clusterRadius: 46,
        // Lets a cluster show that something inside it needs attention.
        clusterProperties: {
          alerts: ['+', ['case', ['==', ['get', 'status'], 'alert'], 1, 0]],
        },
      });
    }

    const add = (spec: LayerSpecification) => {
      if (!map.getLayer(spec.id)) map.addLayer(spec);
    };

    /* ── zoomed out: clusters ──────────────────────────────────────────── */

    add({
      id: FLEET_LAYERS.clusters,
      type: 'circle',
      source: CLUSTER_SOURCE,
      maxzoom: CLUSTER_MAX_ZOOM,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': ['case', ['>', ['get', 'alerts'], 0], palette.clusterAlert, palette.cluster],
        'circle-radius': ['step', ['get', 'point_count'], 15, 10, 19, 50, 25, 200, 32],
        'circle-stroke-width': 2.5,
        'circle-stroke-color': resolved === 'dark' ? '#12141a' : '#ffffff',
        'circle-opacity': 0.92,
      },
    });

    add({
      id: FLEET_LAYERS.clusterCount,
      type: 'symbol',
      source: CLUSTER_SOURCE,
      maxzoom: CLUSTER_MAX_ZOOM,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': MAP_FONT,
        'text-size': 12,
        'text-allow-overlap': true,
      },
      paint: { 'text-color': palette.clusterText },
    });

    // A vehicle far from the rest is its own "cluster of one"; without this it
    // would disappear below the cluster threshold.
    add({
      id: FLEET_LAYERS.clusterSingles,
      type: 'circle',
      source: CLUSTER_SOURCE,
      maxzoom: CLUSTER_MAX_ZOOM,
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-radius': 6,
        // Singles keep the status ramp so a lone idle vehicle still reads as
        // idle when the map is zoomed out past the marker threshold.
        'circle-color': [
          'match',
          ['get', 'status'],
          'moving',
          status.moving,
          'idle',
          status.idle,
          'offline',
          status.offline,
          'alert',
          status.alert,
          palette.cluster,
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': resolved === 'dark' ? '#12141a' : '#ffffff',
      },
    });

    /* ── zoomed in: individual markers ─────────────────────────────────── */

    // A lone marker on a detailed street map is easy to lose, most of all on
    // the dark basemap. Every vehicle sits on a soft glow in its status colour
    // with a crisp ring inside it, so the eye finds the vehicle first and
    // reads the silhouette second. Offline vehicles glow less: they should be
    // findable without competing with the ones that are reporting.
    const statusColour: ExpressionSpecification = [
      'match',
      ['get', 'status'],
      'moving',
      status.moving,
      'idle',
      status.idle,
      'offline',
      status.offline,
      'alert',
      status.alert,
      palette.cluster,
    ];
    const dimWhenOffline = (lit: number, dim: number): ExpressionSpecification => [
      'case',
      ['==', ['get', 'status'], 'offline'],
      dim,
      lit,
    ];

    add({
      id: FLEET_LAYERS.glow,
      type: 'circle',
      source: POINTS_SOURCE,
      minzoom: CLUSTER_MAX_ZOOM,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], CLUSTER_MAX_ZOOM, 20, 16, 34],
        'circle-color': statusColour,
        'circle-blur': 0.75,
        'circle-opacity': dimWhenOffline(resolved === 'dark' ? 0.6 : 0.5, 0.25),
      },
    });

    add({
      id: FLEET_LAYERS.ring,
      type: 'circle',
      source: POINTS_SOURCE,
      minzoom: CLUSTER_MAX_ZOOM,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], CLUSTER_MAX_ZOOM, 13, 16, 20],
        'circle-color': statusColour,
        'circle-opacity': dimWhenOffline(0.16, 0.08),
        'circle-stroke-width': 2,
        'circle-stroke-color': statusColour,
        'circle-stroke-opacity': dimWhenOffline(0.9, 0.45),
      },
    });

    add({
      id: FLEET_LAYERS.pulse,
      type: 'symbol',
      source: POINTS_SOURCE,
      minzoom: CLUSTER_MAX_ZOOM,
      filter: ['==', ['get', 'status'], 'alert'],
      layout: {
        'icon-image': PULSE_ICON,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': 0.8,
      },
    });

    add({
      id: FLEET_LAYERS.selected,
      type: 'circle',
      source: POINTS_SOURCE,
      minzoom: CLUSTER_MAX_ZOOM,
      filter: ['==', ['get', 'id'], '__none__'],
      paint: {
        'circle-radius': 30,
        'circle-color': palette.selected,
        'circle-opacity': 0.16,
        'circle-stroke-width': 1.5,
        'circle-stroke-color': palette.selected,
        'circle-stroke-opacity': 0.55,
      },
    });

    add({
      id: FLEET_LAYERS.markers,
      type: 'symbol',
      source: POINTS_SOURCE,
      minzoom: CLUSTER_MAX_ZOOM,
      layout: {
        'icon-image': ['get', 'icon'],
        // A pin is anchored by its tip rather than its centre, and never
        // rotates — see drawPin.
        'icon-offset': PIN_TIP_OFFSET,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          CLUSTER_MAX_ZOOM,
          MARKER_SIZE_MIN,
          MARKER_SIZE_MAX_ZOOM,
          MARKER_SIZE_MAX,
        ],
      },
    });

    add({
      id: FLEET_LAYERS.labels,
      type: 'symbol',
      source: POINTS_SOURCE,
      minzoom: 13.5,
      layout: {
        'text-field': ['get', 'plate'],
        'text-font': MAP_FONT,
        'text-size': 12,
        'text-offset': [0, 2.1],
        'text-anchor': 'top',
        'text-allow-overlap': false,
        'text-letter-spacing': 0.04,
      },
      paint: {
        'text-color': palette.label,
        'text-halo-color': palette.labelHalo,
        'text-halo-width': 1.4,
      },
    });
  }, [map, ready, styleEpoch, resolved]);

  /**
   * Hit testing is done in screen space against the animator's own state rather
   * than with MapLibre's delegated layer events.
   *
   * A delegated listener re-queries the rendered tiles on each pointer event,
   * and this source is rewritten every animation frame — so the tile is almost
   * always mid-reparse when the pointer moves and the query comes back empty.
   * The upshot was that marker clicks and hovers silently never fired. Our own
   * positions are always current, so this cannot miss.
   */
  const hitTest = useCallback(
    (point: { x: number; y: number }): string | null => {
      if (!map) return null;

      // The pin stands above its coordinate, so a circle around the coordinate
      // would miss the head — which is the part people aim at. Test against the
      // whole shaft instead: the nearest point on the segment from tip to head.
      const size = markerSizeAt(map.getZoom());
      const shaft = PIN_SHAFT * size;
      const radius = PIN_HEAD_RADIUS * size + 8;

      let best: string | null = null;
      let bestDistance = radius * radius;

      for (const entry of animator.current.entries()) {
        const projected = map.project([entry.lng, entry.lat]);
        const nearestY = Math.min(projected.y, Math.max(projected.y - shaft, point.y));
        const dx = projected.x - point.x;
        const dy = nearestY - point.y;
        const distance = dx * dx + dy * dy;
        if (distance < bestDistance) {
          bestDistance = distance;
          best = entry.id;
        }
      }
      return best;
    },
    [map],
  );

  useEffect(() => {
    if (!map || !ready) return;

    const setHovered = (id: string | null) => {
      if (hoveredRef.current === id) return;
      hoveredRef.current = id;
      onHoverRef.current?.(id);
      placeHoverAnchor();
    };

    const handleMouseMove = (event: MapMouseEvent) => {
      // Individual markers only exist above the cluster threshold.
      const id = map.getZoom() > CLUSTER_MAX_ZOOM ? hitTest(event.point) : null;
      map.getCanvas().style.cursor = id ? 'pointer' : '';
      setHovered(id);
    };

    const handleMouseOut = () => {
      map.getCanvas().style.cursor = '';
      setHovered(null);
    };

    const handleClick = async (event: MapMouseEvent) => {
      if (map.getZoom() > CLUSTER_MAX_ZOOM) {
        const id = hitTest(event.point);
        if (id) {
          onSelect(id);
          return;
        }
      }

      // Clusters come from the throttled source, so querying them is safe.
      const clusterLayers = [FLEET_LAYERS.clusters, FLEET_LAYERS.clusterSingles].filter((layer) =>
        map.getLayer(layer),
      );
      const [feature] = clusterLayers.length
        ? map.queryRenderedFeatures(event.point, { layers: clusterLayers })
        : [];

      if (feature?.properties?.cluster_id !== undefined) {
        const source = map.getSource(CLUSTER_SOURCE) as GeoJSONSource;
        const zoom = await source.getClusterExpansionZoom(
          feature.properties.cluster_id as number,
        );
        map.easeTo({
          center: (feature.geometry as GeoJSON.Point).coordinates as [number, number],
          zoom: Math.max(Math.min(zoom + 0.4, 18), CLUSTER_MAX_ZOOM + 0.4),
          duration: 480,
        });
        return;
      }

      if (typeof feature?.properties?.id === 'string') {
        onSelect(feature.properties.id);
        return;
      }

      onSelect(null);
    };

    map.on('mousemove', handleMouseMove);
    map.on('mouseout', handleMouseOut);
    map.on('click', handleClick);

    return () => {
      map.off('mousemove', handleMouseMove);
      map.off('mouseout', handleMouseOut);
      map.off('click', handleClick);
    };
  }, [map, ready, styleEpoch, onSelect, hitTest, placeHoverAnchor]);

  /* Highlight the selected vehicle. */
  useEffect(() => {
    if (!map || !ready || !map.getLayer(FLEET_LAYERS.selected)) return;
    map.setFilter(FLEET_LAYERS.selected, ['==', ['get', 'id'], selectedId ?? '__none__']);
  }, [map, ready, styleEpoch, selectedId]);

  /**
   * One long-lived tween loop. Positions arrive several times a second, so the
   * feed must not own the animation frame: tearing the loop down and
   * rescheduling it on every push starves it and nothing renders. The feed only
   * re-targets the animator and, if the loop has gone idle, kicks it.
   */
  const pump = useCallback(() => {
    if (!map) return;
    const points = map.getSource(POINTS_SOURCE) as GeoJSONSource | undefined;
    if (!points) {
      running.current = false;
      frame.current = null;
      return;
    }

    const { data, animating } = animator.current.step();
    points.setData(data);
    placeHoverAnchor();

    const now = performance.now();
    if (now - lastClusterPush.current > CLUSTER_REFRESH_MS) {
      lastClusterPush.current = now;
      (map.getSource(CLUSTER_SOURCE) as GeoJSONSource | undefined)?.setData(data);
    }

    if (animating) {
      frame.current = requestAnimationFrame(() => pumpRef.current?.());
    } else {
      running.current = false;
      frame.current = null;
    }
  }, [map, placeHoverAnchor]);

  // The loop re-schedules itself, so it reaches the next frame through a ref
  // rather than closing over its own (still-initialising) declaration.
  const pumpRef = useRef<() => void>(pump);
  useEffect(() => {
    pumpRef.current = pump;
  }, [pump]);

  const ensureRunning = useCallback(() => {
    if (running.current) return;
    running.current = true;
    frame.current = requestAnimationFrame(() => pumpRef.current?.());
  }, []);

  useEffect(() => {
    if (!map || !ready) return;

    const inputs: FleetInput[] = vehicles
      .filter((entry) => entry.lat !== null && entry.lng !== null)
      .map((entry) => ({
        id: entry.vehicle.id,
        plateNumber: entry.vehicle.plateNumber,
        status: entry.status,
        lat: entry.lat!,
        lng: entry.lng!,
        speedKph: entry.speedKph,
        updatedAtMs: entry.updatedAt ? Date.parse(entry.updatedAt) : 0,
      }));

    animator.current.setTargets(inputs);
    ensureRunning();
  }, [map, ready, styleEpoch, vehicles, ensureRunning]);

  useEffect(() => {
    if (!map) return;
    map.on('move', placeHoverAnchor);
    return () => {
      map.off('move', placeHoverAnchor);
    };
  }, [map, placeHoverAnchor]);

  /* Stop the loop only when the layer itself goes away. */
  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
      running.current = false;
    },
    [],
  );

  return null;
}
